/**
 * Image Attachment Processing Pipeline
 *
 * Shared by both paste and file picker input methods.
 * Handles: resize → upload attachment → generate preview → return metadata.
 */

import { ulid } from "ulid"
import { downloadNestAttachment, uploadNestAttachment } from "../nest/attachments"
import type { ImageAttachment } from "../types"
import { mimeToExt, resizeImage } from "./imageResize"

export interface ProcessedImage {
    attachment: ImageAttachment
    dataUrl: string
}

export async function loadNestImageBlob(image: ImageAttachment): Promise<Blob | null> {
    if (!image.attachmentId) return null

    const downloaded = await downloadNestAttachment(image.attachmentId)
    return new Blob([downloaded.data], { type: image.mediaType || downloaded.mediaType })
}

export async function loadImageBlob(image: ImageAttachment): Promise<Blob | null> {
    return loadNestImageBlob(image)
}

export async function processImageBlob(blob: Blob): Promise<ProcessedImage> {
    const id = ulid()

    // Resize for API submission
    const resized = await resizeImage(blob)

    // Upload the *resized* image through Nest attachments; appData only stores tiny refs.
    const ext = mimeToExt(resized.mediaType)
    const uploaded = await uploadNestAttachment(resized.blob, { mediaType: resized.mediaType, filename: `${id}.${ext}` })

    // Generate preview data URL from resized blob
    const dataUrl = URL.createObjectURL(resized.blob)

    const attachment: ImageAttachment = {
        id,
        attachmentId: uploaded.attachmentId,
        mediaType: resized.mediaType,
        ext,
        originalWidth: resized.originalWidth,
        originalHeight: resized.originalHeight,
        resizedWidth: resized.width,
        resizedHeight: resized.height,
    }

    return { attachment, dataUrl }
}
